import { apiRequest } from './apiRequest';

export const streamsAPI = {
  list: async (orgId, options: any = {}) => {
    const params = new URLSearchParams();
    if (options.stats) params.set('stats', 'true');
    if (options.stats && options.timeframe) {
      const tf = String(options.timeframe).toLowerCase();
      if (tf === 'week' || tf === 'month' || tf === 'year') params.set('timeframe', tf);
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams${qs}`, { method: 'GET' });
    return data.streams ?? [];
  },
  listDropdown: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams/dropdown`, {
      method: 'GET',
    });
    return data.streams ?? [];
  },
  get: async (orgId, streamId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams/${streamId}`, {
      method: 'GET',
    });
    return data.stream ?? data;
  },
  /** Stream list card payload (activity window, project stats) for one stream. */
  getCardStats: async (orgId, streamId, options: any = {}) => {
    const params = new URLSearchParams();
    const tf = String(options.timeframe || 'month').toLowerCase();
    if (tf === 'week' || tf === 'month' || tf === 'year') params.set('timeframe', tf);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${encodeURIComponent(streamId)}/card-stats${qs}`,
      { method: 'GET' }
    );
    return data.stream ?? null;
  },
  create: async (orgId, body) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return data.stream ?? data;
  },
  update: async (orgId, streamId, body) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams/${streamId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return data.stream ?? data;
  },
  delete: async (orgId, streamId) => {
    await apiRequest<any>(`/api/organisations/${orgId}/streams/${streamId}`, { method: 'DELETE' });
  },
  getMembers: async (orgId, streamId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams/${streamId}/members`, {
      method: 'GET',
    });
    return data.members ?? [];
  },
  addMember: async (orgId, streamId, userId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/streams/${streamId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
    return data.members ?? [];
  },
  removeMember: async (orgId, streamId, userId) => {
    await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/members/${encodeURIComponent(userId)}`,
      {
        method: 'DELETE',
      }
    );
  },
  getDora: async (orgId, streamId, windowDays = 30) => {
    const params = new URLSearchParams({ windowDays: String(windowDays) });
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/dora?${params}`,
      { method: 'GET' }
    );
    return { dora: data.dora, meta: data.meta };
  },
  getDoraLabelMetrics: async (orgId, streamId, options: any = {}) => {
    const params = new URLSearchParams();
    if (options.windowDays != null) params.set('windowDays', String(options.windowDays));
    if (options.granularity === 'week') params.set('granularity', 'week');
    const qs = params.toString();
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/dora-label-metrics${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return { labelDora: data.labelDora, meta: data.meta };
  },
  getCycleTime: async (orgId, streamId, options: any = {}) => {
    const params = new URLSearchParams();
    if (options.windowDays != null) params.set('windowDays', String(options.windowDays));
    const qs = params.toString();
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/cycle-time${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return { cycleTime: data.cycleTime, meta: data.meta };
  },
  getChurn: async (orgId, streamId, windowDays = 30) => {
    const params = new URLSearchParams({ windowDays: String(windowDays) });
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/streams/${streamId}/churn?${params}`,
      { method: 'GET' }
    );
    const churn = data.churn ?? data;
    return { dates: churn.dates ?? [], ratios: churn.ratios ?? [] };
  },
};
